import { PlatformAuthorizer } from '../PlatformAuthorizer';
import {
  HttpRequest,
  HttpHeaders,
  HttpResponse,
  HttpClient,
  Error,
  isError,
  createError,
  ErrorCodes,
} from '@quickplay/foundation';
import { UserAuthorizationData } from '../UserAuthorization';
import { AuthorizationData } from '../OAuth2/model/AuthorizationData';
import { PlatformErrorCodes } from '../PlatformErrorCodes';

const HTTP_HEADER_AUTHORIZATION_KEY = 'Authorization';
const HTTP_HEADER_X_AUTHORIZATION_KEY = 'X-Authorization';
const HTTP_HEADER_X_CLIENT_ID_KEY = 'X-Client-Id';
const HTTP_HEADER_BEARER_AUTH_PREFIX = 'Bearer';

const UNAUTHORIZED_ACCESS_MESSAGE = 'Unauthorized access';
const NETWORK_ERROR_MESSAGE = 'Network error has occured';
const UNAUTHROIZED_HTTP_STATUS_CODE = 401;

function processFailureResponse(platformAuthorizer: PlatformAuthorizer, error: any): Error {
  if (error.code && error.code === UNAUTHROIZED_HTTP_STATUS_CODE) {
    platformAuthorizer.dispose();

    return createError(
      PlatformErrorCodes.PLATFORM_SERVICE_INVALID_ACCESS_TOKEN | ErrorCodes.ERROR_CATEGORY_MASK_SERVER,
      UNAUTHORIZED_ACCESS_MESSAGE,
      error,
    );
  }
  return createError(
    PlatformErrorCodes.PLATFORM_SERVICE_CALL_INTERRUPTED | ErrorCodes.ERROR_CATEGORY_MASK_NETWORK,
    NETWORK_ERROR_MESSAGE,
    error,
  );
}

export async function makeAuthorizedHttpCall<RequestBody, ResponseBody>(
  httpClient: HttpClient<RequestBody, ResponseBody>,
  platformAuthorizer: PlatformAuthorizer,
  request: HttpRequest<RequestBody>,
): Promise<HttpResponse<ResponseBody>> {
  try {
    const [authResponse, userAuthResponse] = await Promise.all([
      platformAuthorizer.ensureAuthorization(),
      platformAuthorizer.userAuthorizationDelegate.fetchUserAuthorizationToken(),
    ]);

    if (isError(authResponse)) {
      return Promise.reject(authResponse);
    }
    if (isError(userAuthResponse)) {
      return Promise.reject(userAuthResponse as Error);
    }
    const userAuthorizationData = userAuthResponse as UserAuthorizationData;
    const authorizationData = authResponse as unknown as AuthorizationData;

    const headers: HttpHeaders = {
      [HTTP_HEADER_AUTHORIZATION_KEY]: `${HTTP_HEADER_BEARER_AUTH_PREFIX} ${authorizationData.accessToken}`,
      [HTTP_HEADER_X_CLIENT_ID_KEY]: platformAuthorizer.platformConfig.xClientId,
      [HTTP_HEADER_X_AUTHORIZATION_KEY]: userAuthorizationData.accessToken,
      ...platformAuthorizer.platformConfig.headers,
    };

    Object.assign((request.headers ??= {}), headers);

    return httpClient.callWith(request);
  } catch (error) {
    return Promise.reject(processFailureResponse(platformAuthorizer, error));
  }
}
